import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

export const exportSalariosToPDF = async (loncheados, filters, empleados) => {
  const doc = new jsPDF();
  
  // Header
  doc?.setFontSize(20);
  doc?.text('Las Dos Doncellas', 105, 20, { align: 'center' });
  
  doc?.setFontSize(16);
  doc?.text('Nómina / Resumen de Salarios', 105, 30, { align: 'center' });
  
  doc?.setFontSize(10);
  const periodLabel = getPeriodLabel(filters);
  doc?.text(`Periodo: ${periodLabel}`, 105, 38, { align: 'center' });
  doc?.text(`Generado: ${new Date()?.toLocaleString('es-ES')}`, 105, 44, { align: 'center' });
  
  let yPosition = 55;
  
  // Calculate totals
  const totalSalario = loncheados?.reduce((sum, l) => sum + l?.salario, 0);
  const totalLoncheados = loncheados?.length;
  
  // Employee info (if single employee)
  const uniqueEmpleados = [...new Set(loncheados.map(l => l.empleadoId))];
  let empleadoNombre = '';
  if (uniqueEmpleados?.length === 1) {
    const empleado = empleados?.find(e => e?.id === uniqueEmpleados?.[0]);
    if (empleado) {
      empleadoNombre = empleado?.nombre;
      doc?.setFontSize(12);
      doc?.text(`Empleado: ${empleadoNombre}`, 20, yPosition);
      yPosition += 10;
    }
  }
  
  // Summary Section
  doc?.setFontSize(14);
  doc?.text('Resumen', 20, yPosition);
  yPosition += 10;
  
  doc?.setFontSize(10);
  doc?.text(`Total € Ganados: ${formatCurrency(totalSalario)}`, 20, yPosition);
  yPosition += 7;
  doc?.text(`Número de Loncheados: ${totalLoncheados}`, 20, yPosition);
  yPosition += 7;
  doc?.text(`Media € por Loncheado: ${formatCurrency(totalSalario / totalLoncheados)}`, 20, yPosition);
  yPosition += 12;
  
  // Detailed Breakdown
  doc?.setFontSize(14);
  doc?.text('Detalle de Loncheados', 20, yPosition);
  yPosition += 10;
  
  doc?.setFontSize(8);
  const headers = ['Fecha', 'Cliente', 'Producto', 'Tipo', 'Peso', 'Importe'];
  const colWidths = [30, 40, 35, 25, 20, 25];
  let xPosition = 20;
  
  headers?.forEach((header, i) => {
    doc?.text(header, xPosition, yPosition);
    xPosition += colWidths?.[i];
  });
  
  yPosition += 7;
  
  loncheados?.forEach((loncheado) => {
    if (yPosition > 280) {
      doc?.addPage();
      yPosition = 20;
    }
    
    xPosition = 20;
    const date = new Date(loncheado.createdAt)?.toLocaleDateString('es-ES');
    doc?.text(date, xPosition, yPosition);
    xPosition += colWidths?.[0];
    
    doc?.text(loncheado?.clienteNombre || '-', xPosition, yPosition);
    xPosition += colWidths?.[1];
    
    doc?.text(loncheado?.productoNombre || '-', xPosition, yPosition);
    xPosition += colWidths?.[2];
    
    doc?.text(loncheado?.tipo || '-', xPosition, yPosition);
    xPosition += colWidths?.[3];
    
    doc?.text(`${loncheado?.pesoLoncheado?.toFixed(2)} kg`, xPosition, yPosition);
    xPosition += colWidths?.[4];
    
    doc?.text(formatCurrency(loncheado?.salario), xPosition, yPosition);
    
    yPosition += 7;
  });
  
  // Total at bottom
  yPosition += 5;
  doc?.setFontSize(12);
  doc?.setFont(undefined, 'bold');
  doc?.text(`TOTAL: ${formatCurrency(totalSalario)}`, 20, yPosition);
  
  // Footer
  const pageCount = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc?.setPage(i);
    doc?.setFontSize(8);
    doc?.setFont(undefined, 'normal');
    doc?.text(`Página ${i} de ${pageCount}`, 105, 290, { align: 'center' });
  }
  
  // Generate filename with employee name if single employee
  const timestamp = Date.now();
  const filename = empleadoNombre 
    ? `nomina-salarios-${empleadoNombre?.replace(/\s+/g, '-')}-${timestamp}.pdf`
    : `nomina-salarios-${timestamp}.pdf`;
  
  doc?.save(filename);
};

export const exportSalariosToCSV = async (loncheados, filters) => {
  const data = loncheados?.map(l => ({
    'Fecha': new Date(l.createdAt)?.toLocaleString('es-ES'),
    'Cliente': l?.clienteNombre || '-',
    'Empleado': l?.empleadoNombre || '-',
    'Producto': l?.productoNombre || '-',
    'Tipo': l?.tipo || '-',
    'Peso (kg)': l?.pesoLoncheado?.toFixed(2) || '0.00',
    'Importe (€)': l?.salario?.toFixed(2) || '0.00'
  }));
  
  // Add total row
  const totalSalario = loncheados?.reduce((sum, l) => sum + l?.salario, 0);
  data?.push({
    'Fecha': '',
    'Cliente': '',
    'Empleado': '',
    'Producto': '',
    'Tipo': 'TOTAL',
    'Peso (kg)': '',
    'Importe (€)': totalSalario?.toFixed(2)
  });
  
  const worksheet = XLSX?.utils?.json_to_sheet(data);
  const workbook = XLSX?.utils?.book_new();
  XLSX?.utils?.book_append_sheet(workbook, worksheet, 'Salarios');
  
  // Generate filename with employee name if single employee
  const uniqueEmpleados = [...new Set(loncheados.map(l => l.empleadoNombre))];
  const timestamp = Date.now();
  const filename = uniqueEmpleados?.length === 1 && uniqueEmpleados?.[0] !== '-'
    ? `salarios-${uniqueEmpleados?.[0]?.replace(/\s+/g, '-')}-${timestamp}.csv`
    : `salarios-${timestamp}.csv`;
  
  XLSX?.writeFile(workbook, filename);
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR'
  })?.format(amount || 0);
};

const getPeriodLabel = (filters) => {
  if (filters?.period === 'day') return 'Día actual';
  if (filters?.period === 'week') return 'Semana actual';
  if (filters?.period === 'month') return 'Mes actual';
  if (filters?.period === 'custom' && filters?.dateFrom && filters?.dateTo) {
    return `${filters?.dateFrom} a ${filters?.dateTo}`;
  }
  return 'Todos';
};